import {FC, useCallback, useEffect} from 'react';
import styled from 'styled-components';

import {startOfUtcDay, startOfUtcHour} from '@shared/lib/utils/date_utils';
import {neverHappens, neverHappensSilent} from '@shared/lib/utils/type_utils';
import {CounterBreakdown} from '@shared/models/api_models';

import {ResponsiveType, useResponsive} from '@shared_web/breakpoints';
import {Button} from '@shared_web/components/core/button';
import {DateTimeInput} from '@shared_web/components/core/date_input';
import {Flex} from '@shared_web/components/core2/flex';
import {useStateRef} from '@shared_web/lib/use_state_ref';

import {translate, Translate} from '@mavismart_frontend/components/translate';
import {INPUT_THEME, MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';

export interface DoorChartFormData {
  breakdown: CounterBreakdown;
  startTs: number;
  endTs: number;
}

interface DoorChartFormProps {
  initialData?: Partial<DoorChartFormData>;
  onChange: (newData: DoorChartFormData) => void;
}

const DATA_POINTS_COUNT: Record<
  ResponsiveType,
  Record<CounterBreakdown, {min: number; niceLooking: number; max: number}>
> = {
  mobile: {
    day: {min: 7, niceLooking: 18, max: 200},
    hour: {min: 8, niceLooking: 14, max: 200},
  },
  tablet: {
    day: {min: 7, niceLooking: 31, max: 200},
    hour: {min: 8, niceLooking: 24, max: 200},
  },
  desktop: {
    day: {min: 7, niceLooking: 31, max: 200},
    hour: {min: 8, niceLooking: 24, max: 200},
  },
};

export const DoorChartForm: FC<DoorChartFormProps> = props => {
  const {initialData, onChange} = props;

  const account = useAccount();
  const lang = getUserLanguage(account);
  const locale = getUserLocale(account);
  const {type} = useResponsive();
  const [breakdown, setBreakdown, breakdownRef] = useStateRef<CounterBreakdown>(
    initialData?.breakdown ?? 'day'
  );

  const [startTs, setStartTs, startTsRef] = useStateRef<number | undefined>(initialData?.startTs);
  const [endTs, setEndTs, endTsRef] = useStateRef<number | undefined>(initialData?.endTs);

  const updateTs = useCallback(
    (ts: number, direction: 'past' | 'future', dataPointsCount: number): number => {
      const delta = (direction === 'past' ? -1 : 1) * (dataPointsCount - 1);
      if (breakdownRef.current === 'day') {
        const newDate = new Date(ts);
        newDate.setUTCDate(newDate.getUTCDate() + delta);
        return newDate.getTime();
      }
      if (breakdownRef.current === 'hour') {
        const newDate = new Date(ts);
        newDate.setUTCHours(newDate.getUTCHours() + delta);
        return newDate.getTime();
      }
      neverHappensSilent(breakdownRef.current);
      return ts;
    },
    [breakdownRef]
  );

  const getDataPointsCount = useCallback(
    (startTs: number, endTs: number): number => {
      if (breakdownRef.current === 'day') {
        const end = startOfUtcDay(new Date(endTs)).getTime();
        const start = startOfUtcDay(new Date(startTs)).getTime();
        return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
      }
      if (breakdownRef.current === 'hour') {
        const end = startOfUtcHour(new Date(endTs)).getTime();
        const start = startOfUtcHour(new Date(startTs)).getTime();
        return Math.round((end - start) / (1000 * 60 * 60)) + 1;
      }
      neverHappensSilent(breakdownRef.current);
      return 0;
    },
    [breakdownRef]
  );

  const updateRange = useCallback(
    (params: {startTs?: number | undefined; endTs?: number | undefined}) => {
      const dataPointsCount = DATA_POINTS_COUNT[type][breakdownRef.current];

      // Extract and normalize the start and end timestamps
      let start = params.startTs;
      let end = params.endTs;
      if (start !== undefined && Number.isNaN(start)) {
        start = undefined;
      }
      if (end !== undefined && Number.isNaN(end)) {
        end = undefined;
      }

      // If both start and end are undefined, set the end to the current date
      if (start === undefined && end === undefined) {
        const newEndDate =
          breakdownRef.current === 'day'
            ? startOfUtcDay(new Date())
            : breakdownRef.current === 'hour'
              ? startOfUtcHour(new Date())
              : neverHappens(breakdownRef.current);
        end = newEndDate.getTime();
      }

      // Determine which timestamp to adjust based on the presence of end
      const tsToAdujst = end !== undefined ? 'start' : 'end';

      // Before we commence, if start or end is undefined, use the current value from the ref
      if (start === undefined) {
        start = startTsRef.current;
      }
      if (end === undefined) {
        end = endTsRef.current;
      }

      // BEGINNING OF THE LOGIC
      if (end !== undefined) {
        if (start !== undefined) {
          // START & END are defined
          const dataPoints = getDataPointsCount(start, end);
          if (dataPoints > dataPointsCount.max) {
            if (tsToAdujst === 'end') {
              end = updateTs(start, 'future', dataPointsCount.max);
            } else {
              start = updateTs(end, 'past', dataPointsCount.max);
            }
          } else if (dataPoints < dataPointsCount.min) {
            const adjustMode = dataPoints <= 1 ? dataPointsCount.niceLooking : dataPointsCount.min;
            if (tsToAdujst === 'end') {
              end = updateTs(start, 'future', adjustMode);
            } else {
              start = updateTs(end, 'past', adjustMode);
            }
          } else {
            // time range is ok
          }
        } else {
          // END ONLY is defined
          start = updateTs(end, 'past', dataPointsCount.niceLooking);
        }
      } else if (start !== undefined) {
        // START ONLY is defined
        end = updateTs(start, 'future', dataPointsCount.niceLooking);
      } else {
        // NONE are defined
        console.error('both start and end are undefined', {
          params,
          start,
          end,
          dataPointsCount,
          tsToAdujst,
        });
        return;
      }

      setStartTs(start);
      setEndTs(end);
    },
    [updateTs, getDataPointsCount, breakdownRef, setStartTs, setEndTs, startTsRef, endTsRef, type]
  );

  const handleStartChange = useCallback(
    (nextStart: number | undefined) => {
      if (!nextStart) {
        // let reset logic handle fully cleared range
        setStartTs(undefined);
        setEndTs(undefined);
        return;
      }
      updateRange({startTs: nextStart});
    },
    [setStartTs, setEndTs, updateRange]
  );

  const handleEndChange = useCallback(
    (nextEnd: number | undefined) => {
      if (!nextEnd) {
        setEndTs(undefined);
        return;
      }
      updateRange({endTs: nextEnd});
    },
    [setEndTs, updateRange]
  );

  const setBreakdownToDay = (e: React.MouseEvent<HTMLButtonElement>) => {
    setBreakdown('day');
    updateRange({}); // reset the range to the current date
    e.currentTarget.blur();
  };

  const setBreakdownToHour = (e: React.MouseEvent<HTMLButtonElement>) => {
    setBreakdown('hour');
    updateRange({}); // reset the range to the current date
    e.currentTarget.blur();
  };

  // Call the onChange callback when the form data changes
  useEffect(() => {
    if (!(startTs && endTs)) {
      return;
    }
    onChange({breakdown, startTs, endTs});
  }, [breakdown, startTs, endTs, onChange]);

  // Reset the form data when the initial data changes
  useEffect(() => {
    setBreakdown(initialData?.breakdown ?? 'day');
    updateRange({
      startTs: initialData?.startTs,
      endTs: initialData?.endTs,
    });
  }, [initialData, setBreakdown, updateRange]);

  return (
    <Flex $column $gap="l" $width="100%" $justify="center" $wrap>
      <SegmentedControl>
        <BreakdownButton
          onClick={setBreakdownToDay}
          $isActive={breakdown === 'day'}
          style={{
            borderRight: 'none',
            borderRadius: '8px 0 0 8px',
            boxShadow: breakdown === 'day' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
            zIndex: breakdown === 'day' ? 1 : 0,
          }}
        >
          <Translate value="daily" />
        </BreakdownButton>
        <BreakdownButton
          onClick={setBreakdownToHour}
          $isActive={breakdown === 'hour'}
          style={{
            borderLeft: 'none',
            borderRadius: '0 8px 8px 0',
            boxShadow: breakdown === 'hour' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
            zIndex: breakdown === 'hour' ? 1 : 0,
          }}
        >
          <Translate value="hourly" />
        </BreakdownButton>
      </SegmentedControl>

      <Flex $gap="s" $justify="center">
        <DateTimeInput
          ts={startTs}
          syncState={handleStartChange}
          label={translate('start', lang)}
          themeOverrides={{input: {...INPUT_THEME, lineHeight: 36}}}
          dateAndTimeJoiner={translate('date_and_time_joiner', lang)}
          noTimePicker={breakdown === 'day'}
          utc
          denseUi
          locale={locale}
        />
        <DateTimeInput
          ts={endTs}
          syncState={handleEndChange}
          label={translate('end', lang)}
          themeOverrides={{input: {...INPUT_THEME, lineHeight: 36}}}
          dateAndTimeJoiner={translate('date_and_time_joiner', lang)}
          noTimePicker={breakdown === 'day'}
          utc
          denseUi
          alignPopupRight
          locale={locale}
        />
      </Flex>
    </Flex>
  );
};

DoorChartForm.displayName = 'DoorChartForm';

const SegmentedControl = styled.div`
  display: inline-flex;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  height: 40px;
`;

const BreakdownButton = styled(Button)<{$isActive: boolean}>`
  padding: 0 16px;
  height: 100%;
  cursor: pointer;
  background-color: ${({$isActive}) => ($isActive ? `${MAIN_COLOR}` : 'white')};
  color: ${({$isActive}) => ($isActive ? 'white' : 'black')};

  &:hover,
  &:active {
    background-color: ${({$isActive}) => ($isActive ? `${MAIN_COLOR}` : '#f0f0f0')};
    color: ${({$isActive}) => ($isActive ? 'white' : 'black')};
  }
  border: ${props => (props.$isActive ? `1px solid ${MAIN_COLOR}` : '1px solid #d2d5d9')};
`;
